import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import TimeAgo from 'react-timeago'
import Image from 'next/image'
import { track } from '@vercel/analytics'
import { FaRegTimesCircle, FaArrowAltCircleUp, FaTrophy, FaAppStore, FaGooglePlay } from 'react-icons/fa'
import { HiArrowRight } from 'react-icons/hi'
import { MdOutlineAddReaction } from 'react-icons/md'
import { Avatar, AvatarGroup, Card, CardBody, Badge,
    User, Button, Modal, ModalContent, ModalBody, useDisclosure,
    ModalHeader,ModalFooter, Textarea, ScrollShadow, Skeleton, Spinner } from "@heroui/react"
import { isToday, isYesterday } from 'date-fns'
import emojiData from '@emoji-mart/data'
import Picker from '@emoji-mart/react'

import FeedImages from '@/components/feed-images'
import FeedContent from '@/components/feed-content'
import { textAreaStyle } from '@/src/styles'
import { addParagraphs, formatNumber } from '@/src/formatter'
import useFeedReactions from '@/hooks/use-feed-reactions'
import useFeedComments from '@/hooks/use-feed-comments'
import { useChallenge } from 'contexts/challenge-context'
import { useUser } from 'contexts/user-context-v2'
import { formatMetric, formatRaceTime, displayMeasurement, getLocale } from '@/src/formatter'
import { activityTypeConfig, allActivityTypes, activityTypesDistance } from '@/src/activity-type-config'

const ReactionDisplayAndModal = ({ postId, reactions, addItemReaction, currentUser, removeItemReaction }) => {
  const { user } = useUser()
  const modal = useDisclosure()
  const modalEmoji = useDisclosure()
  
  const [longPressTimeout, setLongPressTimeout] = useState(null)
  const [isLong, setIsLong] = useState(false)
  
  const { data, loading, error, mutate } = useFeedReactions(modal.isOpen && postId, user.token)
  
  const startLongPress = () => {
    setLongPressTimeout(setTimeout(() => setIsLong(true), 700))
  }

  const endLongPress = (reaction) => {
    if (isLong) {
      track('feed-reaction-long-press')
      modal.onOpen()
    } else {
      if (currentUser.reactions && currentUser.reactions[reaction]) {
        removeItemReaction(reaction)
      } else {
        addItemReaction(reaction)
      }
    }
    setIsLong(false)
    clearTimeout(longPressTimeout)
  }

  return (<div className='flex flex-wrap gap-1 mt-1'>
    {Object.keys(reactions).filter(reaction => reactions[reaction] > 0).map((reaction) => {
      return <Button key={reaction} 
        size='sm'
        radius='full'
        className={`bg-chTan-100 min-w-[1rem] ${currentUser.reactions && currentUser.reactions[reaction] ? 'border-2 border-primary bg-primary-50' : 'text-chTan-600'}`}
        startContent={<span className='text-xl'>{reaction}</span>}
        onPressStart={startLongPress}
        onPress={() => endLongPress(reaction)}
        >
          {reactions[reaction] > 1 && <span>{reactions[reaction]}</span>}
      </Button>
    })}
    <Button 
      size='sm'
      radius='full'
      variant='light'
      className='min-w-[1rem]'
      aria-label="React"
      onPress={()  => {
        modalEmoji.onOpen()
      }}
      startContent={<MdOutlineAddReaction className='text-xl font-bold text-chTan-600' />}
      > 
    </Button>

    <Modal shouldBlockScroll={false} className='max-h-[60dvh]' isOpen={modalEmoji.isOpen} onOpenChange={modalEmoji.onOpenChange}>
      <ModalContent>
        <ModalBody className='p-4 pt-10 items-center'>
          <div 
            style={{
              touchAction: 'auto',
              maxHeight: '50vh',
              overflowY: 'scroll',
              WebkitOverflowScrolling: 'touch',
            }}>
            <Picker 
              previewPosition={'none'} 
              autoFocus={false} 
              searchPosition={'none'} 
              data={emojiData} 
              style={{ overflowY: 'auto' }}
              onEmojiSelect={async (obj) => {
                addItemReaction(obj.native)
                modalEmoji.onClose()
              }}
            />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>

    <Modal scrollBehavior='inside' className='max-h-[60dvh]' isOpen={modal.isOpen} onOpenChange={modal.onOpenChange}>
      <ModalContent>
        <ModalHeader>{data.results.length} Reactions</ModalHeader>
        <ModalBody>
          {loading && <div className='flex justify-center mb-10'><Spinner size='lg' /></div>}
          {error && <div className='text-center'>Error loading likes.</div>}
          
          {(!loading && data && Boolean(data.results.length)) && <div className='max-h-[50vh] overflow-y-scroll'>
            {data.results.map((userReaction) => {
              return <div key={JSON.stringify(userReaction)} className='flex mb-4'>
                <div className='grow'>
                  <User   
                    name={userReaction.fullname}
                    description={<>
                      <TimeAgo style={{ fontWeight: 'normal', fontSize: '14px', color: '#999' }} date={userReaction.createdAt} /> 
                    </>}
                    avatarProps={{
                      showFallback: true,
                      name: !userReaction.fullname.match(' Hound') ? userReaction.fullname : null,
                      src: userReaction.photo,
                      size: 'sm'
                    }}
                    className='text-lg'
                  />
                </div>
                <div className='text-cetner text-2xl'>{userReaction.reaction}</div>
              </div>
            })}
          </div>}
        </ModalBody>
      </ModalContent>
    </Modal>
  </div>)
}

const CommentModal = ({ postId, comments, modal, commentFn, modalActive }) => {
  const { user } = useUser()
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalType, setModalType] = useState()
  const lastOpenStateRef = useRef(false)
  const { data, loading, error, mutate } = useFeedComments(modal.isOpen && postId, user.token)
  const divRef = useRef(null);

  useEffect(() => {
    if (modal.isOpen === lastOpenStateRef.current) return
    lastOpenStateRef.current = modal.isOpen
    if (modal.isOpen) {
      modalActive.current = true
      document.documentElement.classList.add('overflow-hidden'); // <html>
      document.body.classList.add('overflow-hidden');
    } else {
      setTimeout(() => {
        modalActive.current = false
      }, 150)
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }

    if (!modalType) {
      const isMobileMedia = window.matchMedia('(max-width: 768px)').matches;
      const isMobileCheck = window.innerWidth <= 768 && 'ontouchstart' in window;
      setModalType((isMobileMedia || isMobileCheck) ? 'panel' : 'modal')
    }
  }, [modal.isOpen, modalType, modalActive]);

  const scrollToTop = () => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }
  return (
    <Modal 
      className={modalType === 'panel' ? 'absolute inset-0 bottom-0 left-0 right-0 top-0 max-h-[100%] mt-0 mb-0 ml-0 mr-0 rounded-none' : 'max-h-[50dvh]'} 
      size={modalType === 'modal' && 'md'} 
      isOpen={modal.isOpen} onOpenChange={modal.onOpenChange}>
      <ModalContent>
        <ModalHeader>{comments ? `${comments} ` : 'Leave a '}Comment{comments > 1 ? 's' : ''}</ModalHeader>
        <ModalBody>
          {loading && <div className='flex justify-center mb-10'><Spinner size='lg' /></div>}
          {error && <div className='text-center'>Error loading comments.</div>}
          {(!loading && data && Boolean(data.results.length)) && <ScrollShadow className=' max-h-[50vh]' ref={divRef}>
            {saving && <div className="max-w-[300px] w-full flex items-center gap-3 mb-8">
              <div>
                <Skeleton className="flex rounded-full w-12 h-12" />
              </div>
              <div className="w-full flex flex-col gap-2">
                <Skeleton className="h-3 w-3/5 rounded-lg" />
                <Skeleton className="h-3 w-4/5 rounded-lg" />
              </div>
            </div>}
            {data && data.results.map((comment) => {
              return <div key={comment.id} className='flex items-start mb-8 gap-2' >
                <div>
                  <Avatar src={comment.user.photo} showFallback name={comment.user.fullname} />
                </div>
                <div className='grow relative'>
                  <span className='text-sm mr-1 font-bold'>{comment.user.fullname}</span>
                  <TimeAgo className='text-sm text-chCaption' date={comment.createdAt} />
                  <div className='text-md'>{addParagraphs(comment.content)}</div>  
                </div>
              </div>
            })}
          </ScrollShadow>}
        </ModalBody>
        <ModalFooter>
          <div className='flex items-center w-full'>
            <div className='pr-1'>
              <Avatar src={user.photo} showFallback name={user.fullname} />
            </div>
            <div className='grow'>
              <Textarea
                classNames={ textAreaStyle }
                minRows={1}
                maxRows={3}
                value={comment}
                placeholder='Comment...'
                onChange={(e) => {
                  setComment(e.target.value)
                }} />
              </div>
            <div className='pl-1'>
              <Button 
                isIconOnly 
                aria-label="Save" 
                color='secondary'
                isDisabled={saving || !comment}
                
                disableAnimation={true}
                onPress={async () => { 
                  scrollToTop()
                  setSaving(true)
                  await commentFn(postId, comment.trim());
                  await mutate()
                  setSaving(false)
                  setComment('')
                  track('feed-comment')
                }}>
                  <FaArrowAltCircleUp className='text-white text-xl' />
              </Button>
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>)
}

const ActivityBlock = ({ activity, isSelf }) => {
  const { user } = useUser()
  const s = new Date(activity.start)
  const locale = getLocale()
  let timestamp = s.toLocaleTimeString(locale, { day: 'numeric', month: 'short', hour: 'numeric', minute:'2-digit' })
  if (isToday(s)) timestamp = `Today at ${s.toLocaleTimeString(locale, { hour: 'numeric', minute:'2-digit' })}`
  if (isYesterday(s)) timestamp = `Yesterday at ${s.toLocaleTimeString(locale, { hour: 'numeric', minute:'2-digit' })}`
  const isDistanceType = activityTypesDistance.includes(activity.activityType)
  
  return <div className=''>
    <div className={`text-xl font-bold ${isSelf ? 'text-primary-50' : 'text-chTan-900'}`}>
      {activity.activityName}
    </div>
    <div>
      <span className='capitalize'>
        {activityTypeConfig[activity.activityType] ? activityTypeConfig[activity.activityType].title.default : activity.activityType}
      </span> • {timestamp}
    </div>  
    
    
    <div className='mt-4 flex flex-row gap-6'>
      {activity.points && <div>
        <div className={`${isSelf ? 'text-primary-50' : 'text-chTan-600'}`}>Points</div>
        <div className={`text-lg font-semibold ${isSelf ? 'text-chTan-200' : 'text-chTan-900'}`}>
          {formatNumber(activity.points, 2)}
        </div>
      </div>}
      {(Boolean(activity.distance) && isDistanceType) && <div>
        <div className={`${isSelf ? 'text-primary-50' : 'text-chTan-600'}`}>Distance</div>
        <div className={`text-lg font-semibold ${isSelf ? 'text-chTan-200' : 'text-chTan-900'}`}>
          {formatMetric(activity.distance, 2, activity.distance < 1000 && user.imperial === false ? 'sm' : 'lg', user.imperial)} {displayMeasurement(user.imperial, activity.distance < 1000 && user.imperial === false ? 'sm' : 'lg', false, true)}
        </div>
      </div>}
      {(allActivityTypes.includes(activity.activityType) || Boolean(activity.duration)) && <div>
        <div className={`${isSelf ? 'text-primary-50' : 'text-chTan-600'}`}>Duration</div>
        <div className={`text-lg font-semibold ${isSelf ? 'text-chTan-200' : 'text-chTan-900'}`}>
          {formatRaceTime(activity.duration)}
        </div>
      </div>}
      {(isDistanceType && !Boolean(activity.points) && Boolean(activity.elevation)) && <div>
        <div className={`${isSelf ? 'text-primary-50' : 'text-chTan-600'}`}>Elevation</div>
        <div className={`text-lg font-semibold ${isSelf ? 'text-chTan-200' : 'text-chTan-900'}`}>
          {formatMetric(activity.elevation, 0, 'sm', user.imperial)} {displayMeasurement(user.imperial, 'sm', false, true)}
        </div>
      </div>}
    </div>
  </div>
}

const CommentReactionContainer = ({ item, current, commentFn, removeItemReaction, addItemReaction, modalComment, modalActive }) => {
  const { user } = useUser()
  let isSelf = Boolean(item.user.id === user.id)
  return <div className={`${isSelf || (item.dataType !== 'Post' && item.dataType !== 'Activity') ? '' : 'ml-14'}`}>
    <div className='flex mt-1'>
      <ReactionDisplayAndModal 
        postId={item.id} 
        reactions={current.reactions}
        currentUser={current.currentUser}
        removeItemReaction={removeItemReaction}
        addItemReaction={addItemReaction}
        />
      
      <Button 
        size='sm'
        radius='full'
        variant='light'
        className='text-[13px] font-bold text-chTan-600 min-w-[.25rem] mt-1 pl-[.25rem] pr-[.25rem]'
        onPress={() => {
          modalComment.onOpen()
        }}
      aria-label="Reply"> 
      Reply
      </Button>
    </div>
  
    <div className='pl-2 mt-1'>
      <AvatarGroup
        isBordered
        max={3}
        renderCount={(count) => (
          <div onClick={() => modalComment.onOpen()} className="text-chTan-600 text-md font-bold ml-2 cursor-pointer">{item.comments} replies</div>
        )}
        total={item.comments}
        >
        {item.commentedUsers?.map((commentUser) => {
          return <Avatar key={commentUser.id} 
            src={commentUser.photo}
            name={commentUser.fullname}
            classNames={{ base: 'ring-chTan-200' }}
            showFallback
            size='sm'
          />
        })}
      </AvatarGroup>
    </div>

    <CommentModal 
      postId={item.id} 
      modal={modalComment}
      comments={item.comments} 
      commentFn={commentFn}
      modalActive={modalActive}
      />
  </div>
}

const JoinInstructions = ({ item, deleteFn  }) => {
  const { challenge } = useChallenge()
  const { user } = useUser()
  const [isNativeApp, setisNativeApp] = useState(false)
  const [pushStatus, setPushStatus] = useState(null)

  useEffect(() => {
    if (window?.isNativeApp) {
      setisNativeApp(true)
      window.setPushStatus = (val) => {
        setPushStatus(val)
      }
      if (window?.ReactNativeWebView) {
        // send request to update native app session
        window?.ReactNativeWebView?.postMessage(JSON.stringify({ check: 'push' }))
      }
    }
  }, [])

  return <Card 
    radius='sm' 
    shadow='none' 
    className='w-full md:w-[650px] mx-auto bg-chCard-background text-chTan-600 mb-8'
  >
    <CardBody className='min-w-[300px] md:min-w-[350px] py-3'>
      <div className='absolute top-2 right-2 cursor-pointer' onClick={() => {
        deleteFn(item.id)
      }}>
        <FaRegTimesCircle className='text-2xl text-chTan-600' />
      </div>
      <div className='text-chTan-800 text-2xl font-bold mb-2'>Hi {user.fname}!</div>
      <div className=''>{item.content}</div>
      <div className='bg-chTan-200 text-chTan-600 p-4 flex items-center mt-2 mb-1 rounded-md'>
        <div className='grow'>Connect your fitness tracker to automatically sync activities.</div>
        <Button 
          href='/apps'
          as={Link}
          className='bg-chTan-300 mx-2'
          isIconOnly><HiArrowRight className='text-xl text-chBlue-600' /></Button>
      </div>
      
      {!isNativeApp && <div className='bg-chTan-200 text-chTan-600 p-4 mb-1 rounded-md flex'>
        <div className='grow'>Download the Challenge Hound app.</div>
        <div className='flex gap-4'>
          <div className='flex justify-center'><a href='https://apps.apple.com/us/app/challenge-hound/id6448111660' target='_new'><FaAppStore className='text-secondary' style={{ fontSize: '30' }} /></a></div>
          <div className='flex justify-center'><a href='https://play.google.com/store/apps/details?id=com.mobile1.challengehound' target='_new'><FaGooglePlay className='text-secondary' style={{ fontSize: '30' }} /></a></div>
        </div>
      </div>}

      {isNativeApp && pushStatus !== 'granted' && <div className='bg-chTan-200 text-chTan-600 p-4 flex items-center mb-1 rounded-md'>
        <div className='grow'>Enable notifications for real-time updates.</div>
        <Button 
          onPress={() => { window.ReactNativeWebView?.postMessage(JSON.stringify({ requestPush: true })) }}
          className='bg-chTan-300 mx-2'
          isIconOnly><HiArrowRight className='text-xl text-chBlue-600' /></Button>
      </div>}

      {isNativeApp && pushStatus === 'granted' && <div className='bg-chTan-200 text-chTan-600 p-4 flex items-center mb-1 rounded-md'>
        <div className='grow'>Log activities from the plus button at the bottom.</div>
      </div>}
      
    </CardBody>
  </Card>
  }

const AutoPost = ({ item, timestamp, current, commentFn, removeItemReaction, addItemReaction, modalComment, modalActive  }) => {
  const { challenge } = useChallenge()
  const { user } = useUser()
  let isSelf = Boolean(item.user.id === user.id)
  
  return <div className={`w-full md:w-[650px] mx-auto mb-8 mt-4`}>
      
    <div className='text-sm text-chTan-600 ml-[56px] mb-1'>
      {(item.dataType === 'Waypoint' && item.isPrivate) && 'Personal Automated Waypoint Notice'}
      {(item.dataType === 'Waypoint' && !item.isPrivate) && 'Automated Waypoint Notice'}
      
      {item.dataType === 'Completed' && <><span className='font-bold'>{item.user.fullname}</span> has completed the challenge!</>}
    </div>
      
    <div className='flex items-center gap-1'>
      <div className='min-w-12'>
        <Image
          width={40} height={40}
          alt={`Post By: ${challenge.challengeName || 'Challenge Hound'}`}
          src={`${process.env.CDN}/${challenge.logo || 'ch-lg-logo.png'}`}
          className='rounded-full w-full max-w-[40px]' />
      </div>
      {item.dataType === 'Joined' && <div className='pl-1 text-chTan-800'>
        <span className='font-bold'>{item.user.fullname}</span> joined the {challenge.group.type} <span className='lowercase'>{timestamp}</span>
      </div>}
      {item.dataType !== 'Joined' && <div className='flex items-center pl-1'> 
        <FeedContent content={item.content} isSelf={isSelf} />
        {(item.dataType === 'Completed' && item.user.photo) && <Badge size='lg' variant='shodow' content={item.dataType === 'Completed' && <FaTrophy className='text-secondary text-xl' />} >
          <User   
            avatarProps={{
              showFallback: true,
              name: !item.user.fullname.match(' Hound') ? item.user.fullname : null,
              src: item.user.photo,
              size: 'lg'
            }}
          /></Badge>}
      </div>}
    </div>
    {(item.id && item.id !== 'fakeId' && !item.isPrivate) ? <div className='' style={{ marginTop: item.dataType === 'Joined' ? '-10px' : '0px', marginLeft: '55px' }}>
      <CommentReactionContainer 
        isSelf={isSelf}
        item={item}
        current={current}
        modalActive={modalActive}
        commentFn={commentFn}
        removeItemReaction={removeItemReaction}
        addItemReaction={addItemReaction}
        modalComment={modalComment} /></div> : <div className='ml-14 mb-4'></div> }
  </div>
}

const FeedItem = ({ item, deleteFn, addReactionFn, removeReactionFn, commentFn, modalActive }) => {
  const { user } = useUser()
  const [current, setCurrent] = useState({
    reactions: item.reactions || {},
    currentUser: item.currentUser || {}
  })
  
  const router = useRouter()
  const modalComment = useDisclosure()

  useEffect(() => {
    setCurrent({
      reactions: item.reactions || {},
      currentUser: item.currentUser || {}
    })
  }, [item.reactions, item.currentUser])

  const addItemReaction = async (reaction) => {
    const updated = { ...current }
    updated.reactions[reaction] = updated.reactions[reaction] ? updated.reactions[reaction] + 1 : 1
    if (!updated.currentUser.reactions) updated.currentUser.reactions = {}
    updated.currentUser.reactions[reaction] = true
    setCurrent(updated)
    await addReactionFn(item.id, reaction)
    track('feed-reaction-add', { dataType: item.dataType })
  }

  const removeItemReaction = async (reaction) => {
    const updated = { ...current }
    updated.reactions[reaction] = updated.reactions[reaction] ? updated.reactions[reaction] - 1 : 0
    if (updated.reactions[reaction] <= 0) delete updated.reactions[reaction]
    if (updated.currentUser.reactions) delete updated.currentUser.reactions[reaction]
    setCurrent(updated)
    await removeReactionFn(item.id, reaction)
    track('feed-reaction-remove', { dataType: item.dataType })
  }

  const s = new Date(item.createdAt)
  const locale = getLocale()
  let timestamp = s.toLocaleTimeString(locale, { day: 'numeric', month: 'short', hour: 'numeric', minute:'2-digit' })
  if (isToday(s)) timestamp = `Today at ${s.toLocaleTimeString(locale, { hour: 'numeric', minute:'2-digit' })}`
  if (isYesterday(s)) timestamp = `Yesterday at ${s.toLocaleTimeString(locale, { hour: 'numeric', minute:'2-digit' })}`
  
  let isSelf = Boolean(item.user.id === user.id)

  if (item.dataType === 'JoinInstructions') {
    return <JoinInstructions item={item} deleteFn={deleteFn} />
  }
  
  if (['Waypoint', 'Joined', 'Completed'].includes(item.dataType)) {
    return <AutoPost item={item}
      timestamp={timestamp}
      current={current}
      modalActive={modalActive}
      removeItemReaction={removeItemReaction}
      addItemReaction={addItemReaction}
      modalComment={modalComment} />
  }

  return <div className={`w-full md:w-[650px] mx-auto mb-4`}>
    <div>
        {/* name and post date */}
        <div className={`text-sm text-chTan-600 mb-1 ${isSelf ? 'text-right mr-2' : 'ml-14'}`}>
          {item.user.id !== user.id ? item.user.fullname : ''}
          <span className={`ml-2 ${isSelf ? 'text-chTan-600' : 'text-chTan-500'}`}>{timestamp}</span>
        </div>
        
        {/* row with headshot and content */}
        <div className={`flex items-end gap-1 ${isSelf ? 'justify-end' : ''}`}>
          <div className='min-w-12'>
            {!isSelf && <>
              {(item.dataType === 'Post' || item.dataType === 'Activity') && <User   
                avatarProps={{
                  showFallback: true,
                  name: !item.user.fullname.match(' Hound') ? item.user.fullname : null,
                  src: item.user.photo,
                  size: 'md'
                }}
              />}
            </>}
          </div>
          <div>
            <Card 
              radius='sm' 
              shadow='none' 
              className={`group ${isSelf ? 'bg-primary text-primary-50' : 'bg-chCard-background text-chTan-600'}`}
              isPressable={Boolean(item.activity)}
              onPress={() => {
                if (!item.activity) return
                router.push(`/activity/${item.activity.id}`)
              }}
            >
              <CardBody className='min-w-[300px] md:min-w-[350px]'>
              {(isSelf && !item.activity) && <div className='absolute top-2 right-2 opacity-0 transition-opacity delay-750 duration-300 group-hover:opacity-100 cursor-pointer' onClick={() => {
                deleteFn(item.id)
              }}>
                <FaRegTimesCircle className='text-primary-300' />
              </div>}

              {item.activity && <ActivityBlock 
                activity={item.activity} 
                isSelf={isSelf} />}

              {Boolean(item.images?.length) && <div 
                className={`mt-2 grid grid-cols-${item.images.length} gap-2 mb-4`}
                onClick={(e) => e.stopPropagation()}
              >
                <FeedImages images={item.images} />   
              </div>}

              <FeedContent content={item.content} isSelf={isSelf} />
              
              </CardBody>
            </Card>
            
            {(isSelf && !item.isPrivate) && <CommentReactionContainer isSelf={isSelf}
              item={item}
              current={current}
              modalActive={modalActive}
              commentFn={commentFn}
              removeItemReaction={removeItemReaction}
              addItemReaction={addItemReaction}
              modalComment={modalComment} />}
          </div>
        </div>
        
        {(!isSelf && item.id && item.id !== 'fakeId' && !item.isPrivate) && <CommentReactionContainer isSelf={isSelf}
          item={item}
          current={current}
          modalActive={modalActive}
          commentFn={commentFn}
          removeItemReaction={removeItemReaction}
          addItemReaction={addItemReaction}
          modalComment={modalComment} />}
      
    </div>
  </div>
    
}

export default FeedItem
